import { endOfDay, parse, startOfDay } from 'date-fns'
import { formatQuery, RuleProcessor, defaultRuleProcessorMongoDBQuery, toArray, RuleGroupTypeAny } from 'react-querybuilder'
import type { CustomField } from './types'

/**
 * Wraps a date string in a MongoDB $dateFromString expression using YYYY-MM-DD format,
 * so the aggregation pipeline can compare it against stored Date values.
 */
interface DateFromStringExpr { $dateFromString: { dateString: string, format: string } }

const dateFromString = (d: string): DateFromStringExpr => ({
  $dateFromString: {
    dateString: d,
    format: '%Y-%m-%dT%H:%M:%S.%LZ'
  }
})

/**
 * Builds the two-element array form expected by MongoDB comparison expressions,
 * e.g. { $gte: ["$filled_date", { $dateFromString: ... }] }.
 * Handles both single values and arrays (for in/between operators).
 */
const dateExpr = (field: string, date: string | string[]): [string, DateFromStringExpr | DateFromStringExpr[]] => {
  return [
    `$${field}`,
    Array.isArray(date) ? date.map(dateFromString) : dateFromString(date)
  ]
}

/**
 * Custom rule processor for react-querybuilder's mongodb_query format.
 * For fields with inputType "date", converts string values to $dateFromString
 * expressions so comparisons work correctly against MongoDB Date fields.
 * Falls back to the default MongoDB rule processor for all other field types.
 */
const dateRuleProcessor: RuleProcessor = (rule, options) => {
  const opts = options ?? {}
  const { field, operator, valueSource } = rule
  const isDateField = valueSource !== 'field' && typeof opts.fieldData?.inputType === 'string' && ['date'].includes(opts.fieldData.inputType)
  const skippedOperators = ['null', 'notNull']
  if (!isDateField || skippedOperators.includes(operator)) {
    return defaultRuleProcessorMongoDBQuery(rule, opts)
  }
  const valueAsDateArray = toArray(rule.value, { retainEmptyStrings: false })
  if (valueAsDateArray.length === 1) {
    valueAsDateArray.push(valueAsDateArray[0])
  }
  const start = startOfDay(parse(valueAsDateArray[0], 'yyyy-MM-dd', new Date())).toISOString()
  const end = endOfDay(parse(valueAsDateArray[1], 'yyyy-MM-dd', new Date())).toISOString()
  switch (operator) {
    case '=':
      return {
        $expr: {
          $and: [
            { $gte: dateExpr(field, start) },
            { $lte: dateExpr(field, end) }
          ]
        }
      }
    case '<':
      return { $expr: { $lt: dateExpr(field, start) } }
    case '<=':
      return { $expr: { $lte: dateExpr(field, end) } }
    case '>':
      return { $expr: { $gt: dateExpr(field, end) } }
    case '>=':
      return { $expr: { $gte: dateExpr(field, start) } }
    case 'between':
      return {
        $expr: {
          $and: [
            { $gte: dateExpr(field, start) },
            { $lte: dateExpr(field, end) }
          ]
        }
      }
    case '!=':
    case 'notBetween': {
      return {
        $expr: {
          $and: [
            { $gte: dateExpr(field, end) },
            { $lte: dateExpr(field, start) }
          ]
        }
      }
    }
  }
  return defaultRuleProcessorMongoDBQuery(rule, opts)
}

/**
 * Converts a query rule group into a MongoDB match object.
 * Uses dateRuleProcessor to handle date fields and numeric parsing.
 */
export const buildMongoQuery = (group: RuleGroupTypeAny, fields: CustomField[]): Record<string, any> => {
  return formatQuery(group, {
    format: 'mongodb_query',
    parseNumbers: true,
    ruleProcessor: dateRuleProcessor,
    fields,
    context: {
      isDateField: [{ inputType: 'date' }]
    }
  })
}
